export const assessPlayingStyleSuitability = (playerRankings) => {
  return {
    coreStyle: calculateCoreStyle(playerRankings),
    attackingStyle: calculateAttackingStyle(playerRankings),
    defensiveStyle: calculateDefensiveStyle(playerRankings),
    crossing: calculateCrossingSuitability(playerRankings),
    inTransition: calculateTransitionSuitability(playerRankings),
    inPossession: calculateInPossessionInstructionSuitability(playerRankings),
    outOfPossession: calculateOutOfPossessionInstructionSuitability(playerRankings),
  };
}


const meanAverage = (...values) => {
  const sum = values.reduce((a, b) => a + b, 0);
  return sum / values.length || 0;
}


const calculateCoreStyle = (playerRankings) => {
  const attSuitability = meanAverage(
    playerRankings.DM.acc.rating,
    playerRankings.DM.pac.rating,
    playerRankings.DMS.fla.rating,
    playerRankings.DMS.vis.rating, 
    playerRankings.DMS.cro.rating,
    playerRankings.DMS.dri.rating,
    playerRankings.DMS.fin.rating,
    playerRankings.DMS.fir.rating,
    playerRankings.DMS.hea.rating,
    playerRankings.DMS.lon.rating,
    playerRankings.DMS.mar.rating,
    playerRankings.DMS.pas.rating,
    playerRankings.DMS.tck.rating,
    playerRankings.DMS.tec.rating,
  );

  const defSuitability = meanAverage(
    playerRankings.DM.jum.rating,
    playerRankings.DM.str.rating,
    playerRankings.MS.acc.rating,
    playerRankings.MS.pac.rating,
  );

  const balSuitability = 100 - Math.abs(attSuitability - defSuitability);

  return {
    attacking: attSuitability,
    defensive: defSuitability,
    balanced: balSuitability,
  };
}


const calculateAttackingStyle = (playerRankings) => {
  const directSuitability = meanAverage(
    playerRankings.MS.acc.rating,
    playerRankings.MS.agi.rating,
    playerRankings.MS.bal.rating,
    playerRankings.MS.jum.rating,
    playerRankings.MS.pac.rating,
    playerRankings.MS.sta.rating,
    playerRankings.MS.str.rating,
    playerRankings.MS.agg.rating,
    playerRankings.MS.bra.rating,
    playerRankings.MS.wor.rating,
    playerRankings.MS.cro.rating,
    playerRankings.MS.dri.rating,
    playerRankings.MS.hea.rating,
  );

  const shortSuitability = meanAverage(
    playerRankings.MS.ant.rating,
    playerRankings.MS.cnt.rating,
    playerRankings.MS.dec.rating,
    playerRankings.MS.fla.rating,
    playerRankings.MS.otb.rating,
    playerRankings.MS.tea.rating,
    playerRankings.MS.vis.rating,
    playerRankings.MS.fir.rating,
    playerRankings.MS.pas.rating,
    playerRankings.MS.tec.rating,
  );
  
  const mixedSuitability = 100 - Math.abs(directSuitability - shortSuitability);

  return {
    direct: directSuitability,
    short: shortSuitability,
    mixed: mixedSuitability,
  };
}


const calculateDefensiveStyle = (playerRankings) => {
  const aggressiveSuitability = meanAverage(
    playerRankings.DM.acc.rating,
    playerRankings.DM.pac.rating,
    playerRankings.DM.sta.rating,
    playerRankings.DM.agg.rating,
    playerRankings.DM.ant.rating,
    playerRankings.DM.wor.rating,
    playerRankings.DM.tck.rating,
  );

  const cautiousSuitability = meanAverage(
    playerRankings.DM.jum.rating,
    playerRankings.DM.str.rating,
    playerRankings.DM.cmp.rating,
    playerRankings.DM.cnt.rating,
    playerRankings.DM.hea.rating,
    playerRankings.DM.mar.rating,
  );
  
  const mixedSuitability = 100 - Math.abs(aggressiveSuitability - cautiousSuitability);

  return {
    aggressive: aggressiveSuitability,
    cautious: cautiousSuitability,
    mixed: mixedSuitability,
  };
}


const calculateCrossingSuitability = (playerRankings) => {
  const floated = meanAverage(
    playerRankings.S.str.rating,
    playerRankings.S.jum.rating,
    playerRankings.S.agg.rating,
    playerRankings.S.bra.rating,
    playerRankings.S.wor.rating,
    playerRankings.S.hea.rating,
  )

  const whipped = meanAverage(
    playerRankings.S.acc.rating,
    playerRankings.S.agi.rating,
    playerRankings.S.bal.rating,
    playerRankings.S.pac.rating,
    playerRankings.MS.ant.rating,
    playerRankings.MS.dec.rating,
    playerRankings.MS.fla.rating,
    playerRankings.MS.tea.rating,
    playerRankings.MS.vis.rating,
    playerRankings.S.agg.rating,
    playerRankings.S.bra.rating,
    playerRankings.S.wor.rating,
    playerRankings.S.ant.rating,
    playerRankings.S.dec.rating,
    playerRankings.S.tea.rating,
    playerRankings.S.otb.rating,
    playerRankings.MS.cro.rating,
    playerRankings.MS.pas.rating,
    playerRankings.MS.tec.rating,
    playerRankings.S.hea.rating,
    playerRankings.S.tec.rating,
  );

  const low = meanAverage(
    playerRankings.S.acc.rating,
    playerRankings.S.agi.rating,
    playerRankings.S.bal.rating,
    playerRankings.S.pac.rating,
    playerRankings.S.ant.rating,
    playerRankings.S.dec.rating,
    playerRankings.S.tea.rating,
    playerRankings.S.otb.rating,
    playerRankings.S.tec.rating,
    playerRankings.S.fir.rating,
  );

  const mixed = 100 - Math.max(floated, whipped, low);

  return {
    floated,
    whipped,
    low,
    mixed,
  };
}


const calculateTransitionSuitability = (playerRankings) => {
  const counterPress = meanAverage(
    playerRankings.MS.acc.rating,
    playerRankings.MS.agi.rating,
    playerRankings.MS.bal.rating,
    playerRankings.MS.pac.rating,
    playerRankings.MS.agg.rating,
    playerRankings.MS.ant.rating,
    playerRankings.MS.bra.rating,
    playerRankings.MS.dec.rating,
    playerRankings.MS.pos.rating,
    playerRankings.MS.wor.rating,
    playerRankings.MS.tck.rating,
  );

  const regroup = meanAverage(
    playerRankings.MS.jum.rating,
    playerRankings.MS.str.rating,
    playerRankings.MS.cmp.rating,
    playerRankings.MS.cnt.rating,
    playerRankings.MS.hea.rating,
    playerRankings.MS.mar.rating,
  );

  const counter = meanAverage(
    playerRankings.MS.acc.rating,
    playerRankings.MS.agi.rating,
    playerRankings.MS.bal.rating,
    playerRankings.MS.pac.rating,
    playerRankings.MS.otb.rating,
    playerRankings.MS.wor.rating,
    playerRankings.MS.dri.rating,
    playerRankings.MS.tec.rating,
  );

  const holdShape = meanAverage(
    playerRankings.MS.ant.rating,
    playerRankings.MS.dec.rating,
    playerRankings.MS.fla.rating,
    playerRankings.MS.tea.rating,
    playerRankings.MS.vis.rating,
    playerRankings.MS.pas.rating,
  );

  const distributeQuickly = meanAverage(
    playerRankings.MS.cmp.rating,
    playerRankings.MS.cnt.rating,
  );

  const rollItOut = meanAverage(
    playerRankings.D.ant.rating,
    playerRankings.D.dec.rating,
    playerRankings.D.tea.rating,
    playerRankings.D.cmp.rating,
    playerRankings.D.cnt.rating,
    playerRankings.D.pas.rating,
  );

  const throwItLong = meanAverage(
    playerRankings.MS.ant.rating,
    playerRankings.MS.cmp.rating,
    playerRankings.MS.cnt.rating,
    playerRankings.MS.dec.rating,
    playerRankings.MS.tea.rating,
    playerRankings.MS.fir.rating,
    playerRankings.MS.pas.rating,
    playerRankings.MS.tec.rating,
    playerRankings.G.thr.rating,
  );

  const takeShortKicks = meanAverage(
    playerRankings.DM.ant.rating,
    playerRankings.DM.cmp.rating,
    playerRankings.DM.cnt.rating,
    playerRankings.DM.dec.rating,
    playerRankings.DM.tea.rating,
    playerRankings.DM.pas.rating,
    playerRankings.G.kic.rating,
    playerRankings.G.pas.rating,
  );

  const takeLongKicks = meanAverage(
    playerRankings.MS.jum.rating,
    playerRankings.MS.str.rating,
    playerRankings.MS.fir.rating,
    playerRankings.MS.hea.rating,
    playerRankings.MS.tec.rating,
    playerRankings.G.kic.rating,
  );


  return {
    counterPress,
    regroup,
    counter,
    holdShape,
    distributeQuickly,
    rollItOut,
    throwItLong,
    takeShortKicks,
    takeLongKicks,
  }
}


const calculateInPossessionInstructionSuitability = (playerRankings) => {

  const attackingWidth = meanAverage(
    playerRankings.MS.ant.rating,
    playerRankings.MS.dec.rating,
    playerRankings.MS.fla.rating,
    playerRankings.MS.otb.rating,
    playerRankings.MS.tea.rating,
    playerRankings.MS.vis.rating,
    playerRankings.DM.ant.rating,
    playerRankings.DM.dec.rating,
    playerRankings.DM.pos.rating,
    playerRankings.DM.tea.rating,
    playerRankings.MS.pas.rating,
    playerRankings.MS.tec.rating,
    playerRankings.DM.mar.rating,
  );

  const passIntoSpace = meanAverage(
    playerRankings.MS.acc.rating,
    playerRankings.MS.agi.rating,
    playerRankings.MS.bal.rating,
    playerRankings.MS.pac.rating,
    playerRankings.MS.ant.rating,
    playerRankings.MS.dec.rating,
    playerRankings.MS.fla.rating,
    playerRankings.MS.otb.rating,
    playerRankings.MS.tea.rating,
    playerRankings.MS.vis.rating,
    playerRankings.MS.pas.rating,
    playerRankings.MS.tec.rating,
  );

  const playOutOfDefence = meanAverage(
    playerRankings.DM.ant.rating,
    playerRankings.DM.cmp.rating,
    playerRankings.DM.cnt.rating,
    playerRankings.DM.dec.rating,
    playerRankings.DM.tea.rating,
  );

  const higherTempo = meanAverage(
    playerRankings.MS.acc.rating,
    playerRankings.MS.agi.rating,
    playerRankings.MS.bal.rating,
    playerRankings.MS.pac.rating,
    playerRankings.MS.ant.rating,
    playerRankings.MS.dec.rating,
    playerRankings.MS.fla.rating,
    playerRankings.MS.otb.rating,
    playerRankings.MS.tea.rating,
    playerRankings.MS.vis.rating,
    playerRankings.MS.fir.rating,
    playerRankings.MS.tec.rating,
  );

  const workBallIntoBox = meanAverage(
    playerRankings.MS.ant.rating,
    playerRankings.MS.dec.rating,
    playerRankings.MS.fla.rating,
    playerRankings.MS.tea.rating,
    playerRankings.MS.vis.rating,
  );

  const hitEarlyCrosses = meanAverage(
    playerRankings.MS.cro.rating,
    playerRankings.MS.pas.rating,
    playerRankings.MS.tec.rating,
  );

  const shootOnSight = meanAverage(
    playerRankings.MS.fin.rating,
    playerRankings.MS.lon.rating,
    playerRankings.MS.tec.rating,
  );

  const runAtDefence = meanAverage(
    playerRankings.MS.acc.rating,
    playerRankings.MS.agi.rating,
    playerRankings.MS.bal.rating,
    playerRankings.MS.pac.rating,
    playerRankings.MS.ant.rating,
    playerRankings.MS.dec.rating,
    playerRankings.MS.fla.rating,
    playerRankings.MS.dri.rating,
    playerRankings.MS.tec.rating,
  );

  const beMoreExpressive = meanAverage(
    playerRankings.MS.ant.rating,
    playerRankings.MS.dec.rating,
    playerRankings.MS.fla.rating,
    playerRankings.MS.otb.rating,
    playerRankings.MS.tea.rating,
    playerRankings.MS.vis.rating,
    playerRankings.MS.dri.rating,
    playerRankings.MS.fin.rating,
    playerRankings.MS.fir.rating,
    playerRankings.MS.lon.rating,
    playerRankings.MS.pas.rating,
    playerRankings.MS.tec.rating,
  );
  
  return {
    attackingWidth,
    passIntoSpace,
    playOutOfDefence,
    higherTempo,
    workBallIntoBox,
    hitEarlyCrosses,
    shootOnSight,
    runAtDefence,
    beMoreExpressive,
  }
}


const calculateOutOfPossessionInstructionSuitability = (playerRankings) => {
  const useOffsideTrap = meanAverage(
    playerRankings.D.acc.rating,
    playerRankings.D.agi.rating,
    playerRankings.D.bal.rating,
    playerRankings.D.pac.rating,
    playerRankings.D.ant.rating,
    playerRankings.D.dec.rating,
    playerRankings.D.pos.rating,
    playerRankings.D.tea.rating,
  );

  const lineOfEngagement = meanAverage(
    playerRankings.MS.acc.rating,
    playerRankings.MS.agi.rating,
    playerRankings.MS.bal.rating,
    playerRankings.MS.pac.rating,
    playerRankings.MS.agg.rating,
    playerRankings.MS.ant.rating,
    playerRankings.MS.bra.rating,
    playerRankings.MS.dec.rating,
    playerRankings.MS.pos.rating,
    playerRankings.MS.wor.rating,
    playerRankings.MS.tck.rating,
  );

  const defensiveLine = meanAverage(
    playerRankings.DM.acc.rating,
    playerRankings.DM.agi.rating,
    playerRankings.DM.bal.rating,
    playerRankings.DM.pac.rating,
    playerRankings.DM.agg.rating,
    playerRankings.DM.ant.rating,
    playerRankings.DM.bra.rating,
    playerRankings.DM.dec.rating,
    playerRankings.DM.pos.rating,
    playerRankings.DM.wor.rating,
    playerRankings.DM.tck.rating,
  );

  const defensiveWidth = meanAverage(
    playerRankings.DM.acc.rating,
    playerRankings.DM.agi.rating,
    playerRankings.DM.bal.rating,
    playerRankings.DM.pac.rating,
    playerRankings.DM.agg.rating,
    playerRankings.DM.ant.rating,
    playerRankings.DM.bra.rating,
    playerRankings.DM.dec.rating,
    playerRankings.DM.pos.rating,
    playerRankings.DM.wor.rating,
    playerRankings.DM.tck.rating,
  );

  const tighterMarking = meanAverage(
    playerRankings.DM.acc.rating,
    playerRankings.DM.agi.rating,
    playerRankings.DM.bal.rating,
    playerRankings.DM.jum.rating,
    playerRankings.DM.pac.rating,
    playerRankings.DM.str.rating,
    playerRankings.DM.agg.rating,
    playerRankings.DM.ant.rating,
    playerRankings.DM.bra.rating,
    playerRankings.DM.cnt.rating,
    playerRankings.DM.dec.rating,
    playerRankings.DM.pos.rating,
    playerRankings.DM.wor.rating,
    playerRankings.DM.mar.rating,
    playerRankings.DM.tck.rating,
  );

  const getStuckIn = meanAverage(
    playerRankings.DM.bal.rating,
    playerRankings.DM.str.rating,
    playerRankings.DM.agg.rating,
    playerRankings.DM.ant.rating,
    playerRankings.DM.bra.rating,
    playerRankings.DM.dec.rating,
    playerRankings.DM.wor.rating,
    playerRankings.DM.tck.rating,
  );

  return {
    useOffsideTrap,
    lineOfEngagement,
    defensiveLine,
    defensiveWidth,
    tighterMarking,
    getStuckIn,
  };
}
